import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { format, parseISO } from 'date-fns';
import { Box, Typography, Card, CardContent, Grid, useTheme } from '@mui/material';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ArcElement
);

interface DailyUsageData {
  date: string;
  requests: number;
  minutes?: number;
  durationMinutes?: number;
  cost: number;
  httpRequests?: number;
  websocketRequests?: number;
  diarizationRequests?: number;
  asyncRequests?: number;
  failedRequests?: number;
  avgProcessingTime?: number;
}

interface EngineUsage {
  engineName: string;
  requests: number;
  minutes: number;
  cost: number;
  percentage: number;
}

interface UsageChartProps {
  dailyUsage: DailyUsageData[];
  engineUsage: EngineUsage[];
  type: 'requests' | 'cost' | 'time' | 'performance' | 'engines' | 'features';
  title: string;
  height?: number;
}

const UsageChart: React.FC<UsageChartProps> = ({ 
  dailyUsage, 
  engineUsage, 
  type, 
  title, 
  height = 400 
}) => {
  const theme = useTheme();

  const chartColors = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: chartColors.primary,
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
      },
    },
    scales: type !== 'engines' && type !== 'features' ? {
      x: {
        type: 'time' as const,
        time: {
          unit: 'day' as const,
          displayFormats: {
            day: 'MMM dd',
          },
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
          callback: function(value: any) {
            if (type === 'cost') {
              return '€' + value.toFixed(2);
            } else if (type === 'time') {
              return value + 'm';
            } else if (type === 'performance') {
              return value + 'ms';
            }
            return value;
          },
        },
      },
    } : undefined,
  }), [type, chartColors.primary]);

  const chartData = useMemo(() => {
    switch (type) {
      case 'requests':
        return {
          labels: dailyUsage.map(d => d.date),
          datasets: [
            {
              label: 'Total Requests',
              data: dailyUsage.map(d => d.requests),
              borderColor: chartColors.primary,
              backgroundColor: chartColors.primary + '20',
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        };

      case 'cost':
        return {
          labels: dailyUsage.map(d => d.date),
          datasets: [
            {
              label: 'Daily Cost (€)',
              data: dailyUsage.map(d => d.cost),
              borderColor: chartColors.warning,
              backgroundColor: chartColors.warning + '20',
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        };

      case 'time':
        return {
          labels: dailyUsage.map(d => d.date),
          datasets: [
            {
              label: 'Processing Time (minutes)',
              data: dailyUsage.map(d => d.minutes || d.durationMinutes || 0),
              borderColor: chartColors.success,
              backgroundColor: chartColors.success + '20',
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        };

      case 'performance':
        return {
          labels: dailyUsage.map(d => d.date),
          datasets: [
            {
              label: 'Avg Processing Time (ms)',
              data: dailyUsage.map(d => d.avgProcessingTime || 0),
              borderColor: chartColors.info,
              backgroundColor: chartColors.info + '20',
              fill: false,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        };

      case 'engines':
        const engineColors = [
          chartColors.primary,
          chartColors.secondary,
          chartColors.success,
          chartColors.warning,
          chartColors.error,
          chartColors.info,
        ];
        
        return {
          labels: engineUsage.map(e => e.engineName),
          datasets: [
            {
              data: engineUsage.map(e => e.cost),
              backgroundColor: engineColors.slice(0, engineUsage.length),
              borderColor: engineColors.slice(0, engineUsage.length),
              borderWidth: 2,
            },
          ],
        };

      case 'features':
        const featuresData = dailyUsage.reduce((acc, day) => {
          acc.http += day.httpRequests || 0;
          acc.websocket += day.websocketRequests || 0;
          acc.diarization += day.diarizationRequests || 0;
          acc.async += day.asyncRequests || 0;
          return acc;
        }, { http: 0, websocket: 0, diarization: 0, async: 0 });

        return {
          labels: ['HTTP', 'WebSocket', 'Diarization', 'Async'],
          datasets: [
            {
              data: [featuresData.http, featuresData.websocket, featuresData.diarization, featuresData.async],
              backgroundColor: [
                chartColors.primary + '80',
                chartColors.secondary + '80',
                chartColors.success + '80',
                chartColors.warning + '80',
              ],
              borderColor: [
                chartColors.primary,
                chartColors.secondary,
                chartColors.success,
                chartColors.warning,
              ],
              borderWidth: 2,
            },
          ],
        };

      default:
        return { labels: [], datasets: [] };
    }
  }, [type, dailyUsage, engineUsage, chartColors]);

  const renderChart = () => {
    if (type === 'engines' || type === 'features') {
      return (
        <Doughnut 
          data={chartData} 
          options={{
            ...chartOptions,
            plugins: {
              ...chartOptions.plugins,
              tooltip: {
                ...chartOptions.plugins?.tooltip,
                callbacks: {
                  label: function(context: any) {
                    const label = context.label || '';
                    const value = context.parsed;
                    const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                    const percentage = ((value / total) * 100).toFixed(1);
                    
                    if (type === 'engines') {
                      return `${label}: €${value.toFixed(2)} (${percentage}%)`;
                    } else {
                      return `${label}: ${value} requests (${percentage}%)`;
                    }
                  },
                },
              },
            },
          }}
        />
      );
    } else if (type === 'requests' || type === 'cost' || type === 'time' || type === 'performance') {
      return (
        <Line data={chartData} options={chartOptions} />
      );
    }
    
    return null;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1a237e' }}>
          {title}
        </Typography>
        <Box sx={{ height: height, position: 'relative' }}>
          {renderChart()}
        </Box>
      </CardContent>
    </Card>
  );
};

export default UsageChart;